/**
 * @fileoverview Pantalla 5 — Respuesta abierta opcional.
 */

import { useState } from "react";
import { useNavigate } from "react-router-dom";

import { AppShell } from "../components/AppShell";
import { LoadingSpinner } from "../components/LoadingSpinner";
import { Button } from "../components/ui/button";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { useRequireSession } from "../hooks/useSession";
import { registrarRespuestaAbierta } from "../lib/api";
import { mensajeDeError } from "../lib/utils-app";

const MAX = 1000;

export function RespuestaAbierta() {
    useRequireSession();
    const navigate = useNavigate();
    const [texto, setTexto] = useState("");
    const [enviando, setEnviando] = useState(false);
    const [error, setError] = useState(/** @type {string | null} */ (null));

    async function handleGuardar() {
        const limpio = texto.trim();
        if (!limpio) {
            navigate("/resumen-final");
            return;
        }
        setEnviando(true);
        setError(null);
        try {
            await registrarRespuestaAbierta(limpio.slice(0, MAX));
            navigate("/resumen-final");
        } catch (e) {
            setError(mensajeDeError(e));
            setEnviando(false);
        }
    }

    function handleSaltar() {
        navigate("/resumen-final");
    }

    const restantes = MAX - texto.length;

    return (
        <AppShell testid="page-respuesta-abierta">
            <div className="space-y-6">
                <div className="space-y-2">
                    <h1 className="font-serif-headline text-2xl sm:text-3xl font-semibold text-foreground">
                        Una última cosa, si quieres compartirla
                    </h1>
                    <p className="text-base text-muted-foreground leading-relaxed">
                        ¿Hay algo importante para ti sobre la nueva Ley de Participación
                        Ciudadana que no apareció en las afirmaciones? Cuéntanos brevemente.
                        Este campo es opcional.
                    </p>
                </div>

                <div className="space-y-2">
                    <Label htmlFor="respuesta-textarea" className="text-sm font-medium">
                        Tu respuesta (opcional)
                    </Label>
                    <Textarea
                        id="respuesta-textarea"
                        data-testid="respuesta-textarea"
                        rows={6}
                        maxLength={MAX}
                        value={texto}
                        onChange={(e) => setTexto(e.target.value)}
                        placeholder="Escribe aquí lo que quieras compartir..."
                        className="text-base leading-relaxed"
                        aria-describedby="contador-respuesta"
                    />
                    <p
                        id="contador-respuesta"
                        aria-live="polite"
                        className="text-xs text-muted-foreground text-right"
                        data-testid="respuesta-contador"
                    >
                        {texto.length}/{MAX} {restantes < 50 ? `· ${restantes} restantes` : ""}
                    </p>
                </div>

                {error ? (
                    <p
                        role="alert"
                        className="text-sm text-destructive bg-destructive/10 rounded-lg p-3"
                        data-testid="respuesta-error"
                    >
                        {error}
                    </p>
                ) : null}

                <div className="flex flex-col sm:flex-row gap-3 pt-2">
                    <Button
                        variant="outline"
                        size="lg"
                        onClick={handleSaltar}
                        disabled={enviando}
                        className="sm:flex-1 h-12"
                        data-testid="saltar-btn"
                    >
                        Saltar
                    </Button>
                    <Button
                        size="lg"
                        onClick={handleGuardar}
                        disabled={enviando}
                        className="sm:flex-1 h-12 font-semibold"
                        data-testid="guardar-respuesta-btn"
                    >
                        {enviando ? (
                            <>
                                <LoadingSpinner size={18} className="mr-2" /> Guardando...
                            </>
                        ) : (
                            "Guardar y continuar"
                        )}
                    </Button>
                </div>
            </div>
        </AppShell>
    );
}
